<template>
  <div class="clientManagement">
    <pageTabHeader>
      <h5>Client Management</h5>
    </pageTabHeader>
    <tabContent>
      <h6>Active Clients</h6>
      <div class="heading-fields">
        <textInput
          v-model="search"
          class="search-field"
          placeholder="Search client list"
          prependInnerIcon="mdi-magnify"
        />
        <dropdownButton
          v-if="selectedClients.length"
          :items="moreActionsItems"
          title="More Actions"
        />
      </div>
      <dataTable
        :headers="headers.filter((header) => header.text != 'Status' || this.environment.toUpperCase() !== 'PRODUCTION')"
        :items="isLoading ? [] : list"
        item-key="ClientInfoRuleId"
        :search="search"
        :loading="isLoading"
        :sort-by="['ClientId']"
        show-select
        v-model="selectedClients"
        @click:row="openTab"
      >
        <template v-slot:item.PromotionStatus="{ value }">
          <tag :class="getPromotionStatusTag(value)" :title="getPromotionStatusDescription(value)" class="promotion-tag">
            {{ value }}
          </tag>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item>
                <v-list-item-title @click="openTab(item)"
                  >Edit</v-list-item-title
                >
              </v-list-item>
              <v-list-item v-if="hasPromotionAccess && nextEnvironment">
                <v-list-item-title
                  @click="
                    promote({
                      ClientId: item.ClientId,
                      ClientInfoRuleId: item.ClientInfoRuleId,
                    })
                  "
                  >Promote to
                  {{ capitalizedNextEnvironment }}</v-list-item-title
                >
              </v-list-item>
              <v-list-item @click="
                discard({
                  ClientId: item.ClientId,
                  ClientInfoRuleId: item.ClientInfoRuleId
                })
              " v-if="item.PromotionStatus!='Promoted'">
                <v-list-item-title> Discard Changes </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </dataTable>
    </tabContent>
  </div>
</template>

<script>
/**
 * @fileoverview Client Management tab view
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";
import capitalize from "lodash/capitalize";
import pageTabHeader from "@/components/page-tab-header";
import tabContent from "@/components/tab-content";
import dataTable from "@/qux-common/qux22/components/organisms/data-table";
import dropdownButton from "@/qux-common/qux22/components/molecules/dropdown-button";
import textInput from "@/qux-common/qux22/components/molecules/input";
import tag from "@/qux-common/qux22/components/molecules/tag";

export default {
  name: "ClientManagement",
  components: {
    dataTable,
    dropdownButton,
    pageTabHeader,
    tabContent,
    tag,
    textInput,
  },
  data() {
    return {
      headers: [
        { text: "Client ID", value: "ClientId" },
        { text: "Client Info Rule ID", value: "ClientInfoRuleId" },
        { text: "Label", value: "ConfigLabel" },
        { text: "Status", value: "PromotionStatus" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      search: "",
      selectedClients: [],
    };
  },
  computed: {
    capitalizedNextEnvironment() {
      return capitalize(this.nextEnvironment);
    },
    hasPromotionAccess() {
      return this.accessRoles && this.accessRoles.includes("ConfigAdmin");
    },
    moreActionsItems() {
      const moreItems = [{ text: "Bulk Update", action: this.openBulkUpdate }];
      if (this.hasPromotionAccess && this.nextEnvironment)
        moreItems.push({
          text: `Promote to ${this.capitalizedNextEnvironment}`,
          action: this.promote,
        });

      return moreItems;
    },
    isLoading() {
      return (
        this.pendingCalls.includes("/client/list") ||
        this.pendingCalls.includes("/tenant/list")
      );
    },
    ...mapGetters({
      list: "getList",
      pendingCalls: "getPendingCallList",
      activeTenantId: "getActiveTenantId",
      environment: "getEnvironment",
      nextEnvironment: "getNextEnvironment",
      accessRoles: "getRoles",
    }),
  },
  methods: {
    getList() {
      this.$store.dispatch("list");
    },
    getPromotionStatusDescription(promotionStatus) {
      if (promotionStatus === "Promoted") {
        return 'These changes have been promoted.';
      } else {
        return 'These changes have not been promoted.';
      }
    },
    getPromotionStatusTag(promotionStatus) {
      if (promotionStatus === "Promoted") {
        return 'promotedTag';
      } else {
        return 'notPromotedTag';
      }
    },
    openTab(item) {
      this.$store.dispatch("openTab", {
        ClientInfoRuleId: item.ClientInfoRuleId,
        ClientId: item.ClientId,
      });
    },
    openBulkUpdate() {
      this.$store.dispatch("bulkUpdate", {
        ClientIds: this.selectedClients.map((client) => client.ClientId),
      });
    },
    discard({ ClientId, ClientInfoRuleId }) {
      this.$store.dispatch("startDiscard", {
          clientId: ClientId,
          clientInfoRuleId: ClientInfoRuleId,
      });
    },
    promote({ ClientId, ClientInfoRuleId } = {}) {
      this.$store.dispatch("startPromote", {
        clients: ClientId
          ? [
              {
                clientId: ClientId,
                clientInfoRuleId: ClientInfoRuleId,
              },
            ]
          : this.selectedClients.map((client) => ({
              clientId: client.ClientId,
              clientInfoRuleId: client.ClientInfoRuleId,
            })),
      });
    },
    openCreateClientModal() {
      this.$store.dispatch("openCreateClientModal");
    },
  },
  watch: {
    activeTenantId() {
      this.getList();
    },
  },
};
</script>

<style lang="scss" scoped>
.clientManagement {
  height: calc(100% - $page-tab-height);
}

.heading-fields {
  margin: $qvo-spacing-8 0 $qvo-spacing-24 0;
  display: flex;
  align-items: center;
  gap: $qvo-spacing-24;
}

.content {
  padding: $qvo-spacing-24;
}

.search-field {
  width: 446px;
}

.v-data-table {
  &::v-deep {
    tr {
      cursor: pointer;
    }
    .promotion-tag .v-chip__content .subtitle-2 {
      color: white;
    }
    .text-start .promotedTag {
      background-color: $qvo-color-branding-400;
    }
    .text-start .notPromotedTag {
      background-color: $qvo-color-yellow-100;
    }
  }
}

.v-list-item {
  cursor: pointer;

  &:hover {
    background: $qvo-color-grey-100;
  }
}
</style>